import { inject, injectable } from "inversify";
import { UserTierService } from ".";
import type { IpfModule } from "..";
import { Constants } from "../constants.generated";
import { isNullOrUndefined } from "../functions/is-null-or-undefined";
import { TYPES } from "../ioc/types";
import { CurrentUserSelectedTier, EnsureTierLevel, SelectedTier } from "../models/app";
import { ServiceBase } from "./ServiceBase";
import { appendTierQueryParams } from "../functions/append-tier-query-params";

/**
 * User tier service, used to manage page tier state and check and secure the users tier.
 */
@injectable()
export class IpfUserTierService extends ServiceBase implements UserTierService {

	private static readonly SelectedTierSessionKey: string = "SelectedTier";

	private readonly _subscribers = new Map<string, (tier: SelectedTier | null) => void>();

	private userTier?: CurrentUserSelectedTier;

	/**
	 * Initialises a new instance of the IpfUserTierService class.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 */
	public constructor(
		@inject(TYPES.IpfModule) private ipfModule: IpfModule
	) {
		super();
	}

	/**
	 * @inheritdoc
	 */
	public getPageTier(): SelectedTier | null {

		const userTier = this.getUserTier();
		const sessionTierJson = sessionStorage.getItem(IpfUserTierService.SelectedTierSessionKey);
		let pageTier: SelectedTier | null;

		if (sessionTierJson) {

			pageTier = JSON.parse(sessionTierJson) as SelectedTier;

			const sesionTier = pageTier as CurrentUserSelectedTier;
			if (this.ipfModule.context.userId !== sesionTier.userId) {

				pageTier = {};
				sessionStorage.removeItem(IpfUserTierService.SelectedTierSessionKey);
			}

			if (pageTier && userTier)
				pageTier = this.ensureSecureSessionTier(userTier, pageTier);
		}
		else {

			pageTier = userTier;
		}

		return pageTier;
	}

	/**
	 * @inheritdoc
	 */
	public getCurrentTier(): SelectedTier | null {

		return this.ipfModule.getData<SelectedTier>(Constants.DataKeyCurrentBroker) ?? this.getPageTier();
	}

	/**
	 * @inheritdoc
	 */
	public setPageTier(pageTier: SelectedTier | null) {

		const userTier = this.getUserTier();
		if (pageTier && userTier) {
			this.ensureSecureSessionTier(userTier, pageTier);
		}

		const currentUserTier = { ...pageTier, userId: this.ipfModule.context.userId } as CurrentUserSelectedTier;
		sessionStorage.setItem(IpfUserTierService.SelectedTierSessionKey, JSON.stringify(currentUserTier));

		// notify all subscribers
		this._subscribers.forEach(subscriber => subscriber(pageTier));

		this.applyTierLinks();
	}

	/**
	 * @inheritdoc
	 */
	public isFullyConstrained(): boolean {

		let t = this.ipfModule.context.userTier;
		return (t?.group?.id && t?.branch?.id && t?.broker?.id) ? true : false;
	}

	/**
	 * @inheritdoc
	 */
	public isAllowedToChangeGroup() {

		return !this.getUserTier()?.group?.id;
	}

	/**
	 * @inheritdoc
	 */
	public isAllowedToChangeBranch() {

		return !this.getUserTier()?.branch?.id;
	}

	/**
	 * @inheritdoc
	 */
	public isAllowedToChangeBroker() {

		return !this.getUserTier()?.broker?.id;
	}

	/**
	 * @inheritdoc
	 */
	public isAllowedToChangePageTier() {

		return (this.isAllowedToChangeGroup() || this.isAllowedToChangeBranch() || this.isAllowedToChangeBroker()) &&
			!this.ipfModule.getData<SelectedTier>(Constants.DataKeyCurrentBroker);
	}

	/**
	 * @inheritdoc
	 */
	public subscribe(id: string, subscriber: (tier: SelectedTier | null) => void) {

		this._subscribers.set(id, subscriber);
	}

	/**
	 * @inheritdoc
	 */
	public ensureTierQueryString(level: EnsureTierLevel = EnsureTierLevel.Broker) {

		const currentTier = this.getCurrentTier();
		const hasCurrentBroker = !isNullOrUndefined(this.ipfModule.getData<SelectedTier>(Constants.DataKeyCurrentBroker));

		const query = window.location.search;
		let params: URLSearchParams;

		if (!query) {

			params = new URLSearchParams("?");
		}
		else {

			params = new URLSearchParams(query.slice(1));
		}

		if (!hasCurrentBroker) {

			if (level >= EnsureTierLevel.Group) {

				this.ensureTierQueryParam(currentTier?.group?.id, params, "tier1");
			}

			if (level >= EnsureTierLevel.Broker) {

				this.ensureTierQueryParam(currentTier?.branch?.id, params, "tier2");
				this.ensureTierQueryParam(currentTier?.broker?.id, params, "tier3");
			}

			if ((level === EnsureTierLevel.Broker && currentTier?.broker?.id) || (level === EnsureTierLevel.Group && currentTier?.group?.id)) {

				const paramString = '?' + params.toString();
				if (window.location.search !== paramString) {

					window.location.search = paramString;
				}
			}
		}

		this.subscribe(nameof(this.ensureTierQueryString), () => {

			this.ensureTierQueryString(level);
		});
	}

	private ensureTierQueryParam(userTierId: string | undefined, params: URLSearchParams, tierParamName: string) {

		if (userTierId) {

			params.set(tierParamName, userTierId);
		}
		else if (params.has(tierParamName)) {

			params.delete(tierParamName);
		}
	}

	private getUserTier(): CurrentUserSelectedTier | null {

		if (!this.userTier) {

			this.userTier = this.ipfModule.context.userTier;
		}
		return this.userTier === undefined ? null : this.userTier;
	}

	private ensureSecureSessionTier(userTier: SelectedTier, sessionTier: SelectedTier) {

		const userBrokerId = userTier.broker?.id;
		const userBranchId = userTier.branch?.id;
		const userGroupId = userTier.group?.id;

		if (userBrokerId && sessionTier.broker?.id !== userBrokerId)
			return userTier;
		else if (userBranchId && sessionTier.branch?.id !== userBranchId)
			return userTier;
		else if (userGroupId && sessionTier.group?.id !== userGroupId)
			return userTier;
		else
			return sessionTier;
	}

	public applyTierLinks() {
		const pageTier = this.getPageTier();
		if (!pageTier) { return; }

		document.querySelectorAll<HTMLLinkElement>("a[data-tier-link]").forEach(element => appendTierQueryParams(element, pageTier));
	}
}